import { useState, useRef, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  Box,
  Typography,
  Modal,
  Button,
  IconButton,
  TextField,
  Switch,
  Popover,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import './EditOneOnOneSchedule.css';
import { axiosInstance } from '../../../../utils/tokenUtils';
// Import time picker components
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import ReactDOM from 'react-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// Setup dayjs plugins for timezone handling
dayjs.extend(utc);
dayjs.extend(timezone);

// Custom renderer that hides the minute hand in the hours view
const customTimeViewRenderer = props => {
  const { view, value, onChange, ampm, ...other } = props;

  // For hours view, render the standard clock
  if (view === 'hours') {
    return renderTimeViewClock({
      view,
      value,
      onChange,
      ampm,
      ...other,
    });
  }

  // For minutes view, which is shown after hours are selected
  return renderTimeViewClock({
    view,
    value,
    onChange,
    ampm,
    ...other,
  });
};

const EditOneOnOneSchedule = ({ open, onClose, availabilityId }) => {
  const [days, setDays] = useState({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  });

  const [timeSlots, setTimeSlots] = useState({
    monday: [{ from: '', to: '' }],
    tuesday: [{ from: '', to: '' }],
    wednesday: [{ from: '', to: '' }],
    thursday: [{ from: '', to: '' }],
    friday: [{ from: '', to: '' }],
    saturday: [{ from: '', to: '' }],
    sunday: [{ from: '', to: '' }],
  });

  const [description, setDescription] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [apiError, setApiError] = useState('');
  const [copyAnchorEl, setCopyAnchorEl] = useState(null);
  const [copySourceDay, setCopySourceDay] = useState('');
  const [copySourceIndex, setCopySourceIndex] = useState(null);
  const [copyTargetDays, setCopyTargetDays] = useState({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [availabilityData, setAvailabilityData] = useState(null);
  const [eventTypeId, setEventTypeId] = useState('');
  const [subject, setSubject] = useState('');
  const [tags, setTags] = useState([]);
  const [levelOfTeaching, setLevelOfTeaching] = useState('');

  // State for currently active time picker to track which one is open
  const [activeTimePicker, setActiveTimePicker] = useState(null);
  const [clockView, setClockView] = useState('hours'); // 'hours' or 'minutes'

  // Add ref for time picker positioning
  const timePickerRefs = useRef({});

  // Reset all state when modal is opened or closed
  useEffect(() => {
    if (!open) {
      resetForm();
      setApiError('');
      setDataFetched(false);
    }
  }, [open]);

  // Fetch existing availability data when modal opens
  useEffect(() => {
    if (open && availabilityId) {
      console.log('Fetching data for availability ID:', availabilityId);
      setIsLoading(true);
      fetchData();
    }
  }, [open, availabilityId]);

  // Fetch both teacher and availability data
  const fetchData = async () => {
    try {
      setApiError('');
      setIsLoading(true);

      // Fetch both data in parallel
      const [teacherResponse, availabilityResponse] = await Promise.all([
        axiosInstance.get('/teachers'),
        axiosInstance.get(`/teachers/availability/${availabilityId}`),
      ]);

      console.log('Teacher data received:', teacherResponse.data);
      console.log('Availability data received:', availabilityResponse.data);

      // Process teacher data if needed
      const teacherData = teacherResponse.data?.data || teacherResponse.data;

      // Process availability data
      const availData = availabilityResponse.data?.data || availabilityResponse.data;

      if (availData) {
        setAvailabilityData(availData);

        // Set event type ID for the PATCH request
        setEventTypeId(availData.id || availData.event_type?.id);

        // Set description and other fields
        setDescription(availData.description || '');
        setSubject(availData.subject || '');
        setTags(availData.tags || []);
        setLevelOfTeaching(availData.levels_to_teach || availData.level_of_teaching || '');

        // Check for time slots in different possible response formats
        const timeSlots = availData.time_slots || [];
        const availability = availData.availability || [];

        if (timeSlots.length > 0) {
          // Process time_slots format
          processAvailabilityData(timeSlots);
          setDataFetched(true);
        } else if (availability.length > 0) {
          // Process availability format
          processAvailabilityArray(availability);
          setDataFetched(true);
        } else {
          console.warn('No time slots or availability found in the response data');
          // Just set empty data instead of showing error
          setDataFetched(true);
        }
      } else {
        console.warn('No data found in the availability response');
        // Still allow editing with empty data
        setDataFetched(true);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Don't block the UI with error, just log it
      console.warn(`Failed to load data: ${error.message || 'Unknown error'}`);
      setDataFetched(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to convert UTC time to local time
  const convertUTCToLocal = timeString => {
    if (!timeString) return null;
    // Assuming timeString is in 'HH:mm' format
    const [hours, minutes] = timeString.split(':');
    const utcDate = dayjs.utc().hour(hours).minute(minutes);
    return utcDate.local().format('hh:mm A');
  };

  // Helper function to convert local time to UTC
  const convertLocalToUTC = timeString => {
    if (!timeString) return null;
    // Assuming timeString is in 'HH:mm' format or '12-hour format with AM/PM'
    let localDate;
    if (timeString.includes('AM') || timeString.includes('PM')) {
      localDate = dayjs(`2023-01-01 ${timeString}`, 'YYYY-MM-DD hh:mm A');
    } else {
      const [hours, minutes] = timeString.split(':');
      localDate = dayjs().hour(hours).minute(minutes);
    }
    return localDate.utc().format('HH:mm');
  };

  // Utility function to convert time string to minutes
  const convertToMinutes = timeString => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Process availability data to populate the form
  const processAvailabilityData = timeSlots => {
    if (!timeSlots || timeSlots.length === 0) {
      console.warn('No time slots to process');
      return;
    }

    console.log('Processing time slots:', timeSlots);

    // Initialize a clean state
    const newDays = {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    };

    // Create a new object to store unique time slots by day
    const processedTimeSlots = {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: [],
    };

    // Process each time slot from the API
    timeSlots.forEach(slot => {
      if (slot.from && slot.to) {
        // Extract day of week from the timestamp
        const fromDate = new Date(slot.from);
        const day = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][
          fromDate.getDay()
        ];

        // Mark this day as active
        newDays[day] = true;

        // Convert UTC times to local time format for display
        const fromLocalTime = dayjs(slot.from).local().format('hh:mm A');
        const toLocalTime = dayjs(slot.to).local().format('hh:mm A');

        console.log(`Converting time for ${day}:`, {
          utc: { from: slot.from, to: slot.to },
          local: { from: fromLocalTime, to: toLocalTime },
        });

        // Check if this time slot already exists for this day
        const timeSlotExists = processedTimeSlots[day].some(
          existingSlot => existingSlot.from === fromLocalTime && existingSlot.to === toLocalTime
        );

        // Only add if it doesn't exist yet
        if (!timeSlotExists) {
          processedTimeSlots[day].push({
            from: fromLocalTime,
            to: toLocalTime,
          });
        }
      }
    });

    // Ensure each day has at least one empty slot if it has no slots but is active
    Object.keys(newDays).forEach(day => {
      if (newDays[day] && processedTimeSlots[day].length === 0) {
        processedTimeSlots[day].push({ from: '', to: '' });
      }
    });

    console.log('Processed days:', newDays);
    console.log('Processed time slots:', processedTimeSlots);

    // Update state
    setDays(newDays);
    setTimeSlots(processedTimeSlots);

    // Debugging check for duplicates
    Object.keys(processedTimeSlots).forEach(day => {
      const slots = processedTimeSlots[day];
      const uniqueSlotStrings = new Set();
      let hasDuplicates = false;

      slots.forEach(slot => {
        const slotString = `${slot.from}-${slot.to}`;
        if (uniqueSlotStrings.has(slotString)) {
          hasDuplicates = true;
          console.error(`Duplicate found for ${day}: ${slotString}`);
        }
        uniqueSlotStrings.add(slotString);
      });

      if (hasDuplicates) {
        console.error(`Day ${day} has duplicate time slots!`);
      }
    });
  };

  // Add function to process availability array format
  const processAvailabilityArray = availability => {
    if (!availability || availability.length === 0) {
      console.warn('No availability array to process');
      return;
    }

    console.log('Processing availability array:', availability);

    // Initialize a clean state
    const newDays = {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    };

    // Create a new object to store time slots by day
    const processedTimeSlots = {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: [],
    };

    // Process each availability slot
    availability.forEach(slot => {
      if (slot.startTime && slot.endTime && slot.days && slot.days.length > 0) {
        // Process each day in the availability
        slot.days.forEach(dayName => {
          // Convert day name to lowercase for our state
          const day = dayName.toLowerCase();

          // Mark this day as active
          if (processedTimeSlots.hasOwnProperty(day)) {
            newDays[day] = true;

            // Convert times to 12-hour format
            const fromLocalTime =
              convertUTCToLocal(slot.startTime) || formatTo12Hour(slot.startTime);
            const toLocalTime = convertUTCToLocal(slot.endTime) || formatTo12Hour(slot.endTime);

            console.log(`Converting time for ${day}:`, {
              from: slot.startTime,
              to: slot.endTime,
              fromLocal: fromLocalTime,
              toLocal: toLocalTime,
            });

            // Add the time slot
            processedTimeSlots[day].push({
              from: fromLocalTime,
              to: toLocalTime,
            });
          }
        });
      }
    });

    // Ensure each active day has at least one empty slot if it has no slots
    Object.keys(newDays).forEach(day => {
      if (newDays[day] && processedTimeSlots[day].length === 0) {
        processedTimeSlots[day].push({ from: '', to: '' });
      }
    });

    console.log('Processed days:', newDays);
    console.log('Processed time slots:', processedTimeSlots);

    // Update state
    setDays(newDays);
    setTimeSlots(processedTimeSlots);
  };

  // Helper to format 24-hour time to 12-hour format
  const formatTo12Hour = time24 => {
    if (!time24) return '';
    try {
      const [hours, minutes] = time24.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      return `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (e) {
      console.error('Error formatting time:', e);
      return time24;
    }
  };

  const handleDayToggle = day => {
    setDays({
      ...days,
      [day]: !days[day],
    });

    // Clear error when toggling days
    setErrorMessage('');
  };

  // Modified handleTimeChange to remove validation for "to time must be after from time"
  const handleTimeChange = (day, index, field, value) => {
    // If value is null, the field is being cleared
    if (!value) {
      const newTimeSlots = { ...timeSlots };
      newTimeSlots[day][index][field] = '';
      setTimeSlots(newTimeSlots);
      setClockView('hours'); // Reset to hours view
      return;
    }

    // For hours selection, update the hours and keep existing minutes or use 00
    if (clockView === 'hours') {
      const currentTimeValue = timeSlots[day][index][field];
      let formattedTime;
      if (currentTimeValue) {
        const existingTime = stringToTime(currentTimeValue);
        formattedTime = existingTime
          ? value.minute(existingTime.minute()).format('hh:mm A')
          : value.minute(0).format('hh:mm A');
      } else {
        formattedTime = value.minute(0).format('hh:mm A');
      }

      const fromTime = stringToTime(formattedTime) || value.minute(0);
      const autoToTime = field === 'from' ? fromTime.add(1, 'hour').format('hh:mm A') : null;

      setTimeSlots(prev => {
        const slot = prev[day][index];
        const updatedSlot = {
          ...slot,
          [field]: formattedTime,
          ...(field === 'from' && !slot.to && autoToTime ? { to: autoToTime } : {}),
        };
        return {
          ...prev,
          [day]: prev[day].map((s, i) => (i === index ? updatedSlot : s)),
        };
      });
      setClockView('minutes');
      return;
    }

    // For minutes selection, format and update the complete time
    const formattedTime = value.format('hh:mm A');

    // When "from" is selected, auto-set "to" to 1 hour later if "to" is empty
    const fromTime = stringToTime(formattedTime) || value;
    const autoToTime = field === 'from' ? fromTime.add(1, 'hour').format('hh:mm A') : null;

    setTimeSlots(prev => {
      const slot = prev[day][index];
      const updatedSlot = {
        ...slot,
        [field]: formattedTime,
        ...(field === 'from' && !slot.to && autoToTime ? { to: autoToTime } : {}),
      };
      return {
        ...prev,
        [day]: prev[day].map((s, i) => (i === index ? updatedSlot : s)),
      };
    });

    // Reset to hours view for next selection
    setClockView('hours');
  };

  // Function to handle opening a time picker
  const handleTimePickerOpen = (day, index, field) => {
    // Reset the current field's value when opening the picker to allow re-selection
    if (timeSlots[day][index][field]) {
      const newTimeSlots = { ...timeSlots };
      // Don't actually clear the value in the UI, just in the internal state when opening
      // This allows the picker to reset while keeping the display value
      setActiveTimePicker({
        day,
        index,
        field,
        previousValue: newTimeSlots[day][index][field], // Store previous value
      });
    } else {
      setActiveTimePicker({ day, index, field });
    }
    setClockView('hours'); // Always start with hours view
  };

  // Function to handle closing a time picker
  const handleTimePickerClose = () => {
    // If user cancels without selecting a new value, restore the previous value
    if (activeTimePicker?.previousValue) {
      const { day, index, field, previousValue } = activeTimePicker;
      const newTimeSlots = { ...timeSlots };

      // Only restore if the value was actually cleared (user didn't select a new time)
      if (!newTimeSlots[day][index][field]) {
        newTimeSlots[day][index][field] = previousValue;
        setTimeSlots(newTimeSlots);
      }
    }

    setActiveTimePicker(null);
    setClockView('hours'); // Reset to hours view
  };

  // Utility function to convert string time to dayjs object
  const stringToTime = timeString => {
    if (!timeString) return null;
    // Parse time including AM/PM format
    return dayjs(`2023-01-01 ${timeString}`, 'YYYY-MM-DD hh:mm A');
  };

  const addTimeSlot = day => {
    const newTimeSlots = { ...timeSlots };
    newTimeSlots[day].push({ from: '', to: '' });
    setTimeSlots(newTimeSlots);
  };

  const removeTimeSlot = (day, index) => {
    const newTimeSlots = { ...timeSlots };
    newTimeSlots[day].splice(index, 1);
    if (newTimeSlots[day].length === 0) {
      newTimeSlots[day].push({ from: '', to: '' });
    }
    setTimeSlots(newTimeSlots);
  };

  const openCopyPopover = (event, day, index) => {
    // Prevent event bubbling
    event.stopPropagation();

    // Set the anchor element to the current target (the copy icon button)
    setCopyAnchorEl(event.currentTarget);
    setCopySourceDay(day);
    setCopySourceIndex(index);

    // Reset target days except the source day
    const newCopyTargetDays = {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    };
    setCopyTargetDays(newCopyTargetDays);

    // Clear any previous errors related to copying
    setErrorMessage('');
  };

  const closeCopyPopover = () => {
    setCopyAnchorEl(null);
    setCopySourceDay('');
    setCopySourceIndex(null);
  };

  const handleCopyTargetChange = day => {
    setCopyTargetDays({
      ...copyTargetDays,
      [day]: !copyTargetDays[day],
    });
  };

  const applyCopy = () => {
    const sourceSlot = timeSlots[copySourceDay][copySourceIndex];

    // Prevent copying if source slot is incomplete
    if (!sourceSlot?.from || !sourceSlot?.to) {
      setErrorMessage(
        'Please select both "From" and "To" times for the source slot before copying.'
      );
      return;
    }

    // Determine selected target days excluding the source day
    const selectedTargets = Object.keys(copyTargetDays).filter(
      day => day !== copySourceDay && copyTargetDays[day]
    );

    if (selectedTargets.length === 0) {
      setErrorMessage('Please select at least one day to copy to.');
      return;
    }

    const newTimeSlots = { ...timeSlots };
    const newDays = { ...days };

    selectedTargets.forEach(day => {
      // Enable the day if it's not already enabled
      newDays[day] = true;

      // Ensure the day has a time slots array
      if (!Array.isArray(newTimeSlots[day])) {
        newTimeSlots[day] = [];
      }

      // If there are no slots yet, create the first one
      if (newTimeSlots[day].length === 0) {
        newTimeSlots[day] = [{ from: sourceSlot.from, to: sourceSlot.to }];
      } else {
        // If there is a single empty default slot, fill it instead of adding a new one
        const hasOnlyEmptyDefault =
          newTimeSlots[day].length === 1 &&
          !newTimeSlots[day][0]?.from &&
          !newTimeSlots[day][0]?.to;

        if (hasOnlyEmptyDefault) {
          newTimeSlots[day][0] = { from: sourceSlot.from, to: sourceSlot.to };
        } else {
          // Avoid adding duplicate slots
          const isDuplicate = newTimeSlots[day].some(
            s => s.from === sourceSlot.from && s.to === sourceSlot.to
          );

          if (!isDuplicate) {
            newTimeSlots[day].push({ from: sourceSlot.from, to: sourceSlot.to });
          }
        }
      }
    });

    setDays(newDays);
    setTimeSlots(newTimeSlots);

    // Clear errors and close popover after successful copy
    setErrorMessage('');
    closeCopyPopover();
  };

  // Remove validation for time slots - any "to" before "from" is now a valid overnight session
  const validateTimeSlots = () => {
    // We're no longer validating that "to time" must be after "from time"
    // This function now just ensures both fields are filled
    let hasErrors = false;

    Object.keys(days).forEach(day => {
      if (days[day]) {
        timeSlots[day].forEach(slot => {
          if (!slot.from || !slot.to) {
            setErrorMessage('Please fill in all time slots');
            hasErrors = true;
          }
        });
      }
    });

    return !hasErrors;
  };

  const validateForm = () => {
    // Check if at least one day is selected
    const anyDaySelected = Object.values(days).some(value => value);
    if (!anyDaySelected) {
      setErrorMessage('Please select at least one day');
      return false;
    }

    // Validate time slots
    if (!validateTimeSlots()) {
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (validateForm()) {
      setIsLoading(true);

      try {
        // Format data according to the expected API format
        const formattedTimeSlots = {};

        // Process each day that's enabled
        Object.keys(days).forEach(day => {
          if (days[day]) {
            // Capitalize first letter of day name
            const capitalizedDay = day.charAt(0).toUpperCase() + day.slice(1);

            // Map time slots to the required format
            const dayTimeSlots = timeSlots[day]
              .filter(slot => slot.from && slot.to) // Only include slots with both times filled
              .map(slot => {
                // Convert local time to UTC format
                const from24 = convertLocalToUTC(slot.from);
                const to24 = convertLocalToUTC(slot.to);

                return {
                  start: from24,
                  end: to24,
                };
              });

            // Only add the day if it has time slots
            if (dayTimeSlots.length > 0) {
              formattedTimeSlots[capitalizedDay] = dayTimeSlots;
            }
          }
        });

        // Prepare data for PATCH request
        const requestData = {
          event_type_id: eventTypeId,
          time_slots: formattedTimeSlots,
          description: description,
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        // Include additional fields if available
        if (tags && tags.length > 0) {
          requestData.tag_ids = tags.map(tag => tag.id);
        }

        if (levelOfTeaching) {
          requestData.level_of_teaching = levelOfTeaching;
        }

        console.log('Sending PATCH request with data:', requestData);

        // Make PATCH API call
        await axiosInstance.patch('/teachers/availability', requestData);

        // Quietly refresh calendar data if function exists
        if (window.refreshCalendarData) {
          window.refreshCalendarData();
        }

        // Show toast notification instead of setting showSuccess state
        toast.success('One on One session updated successfully!');

        // Close modal immediately
        resetForm();
        onClose();
      } catch (error) {
        console.error('Error updating availability:', error);

        // Format error message from API correctly
        let errorMsg = 'Failed to update availability. Please try again.';
        const data = error?.response?.data;
        if (data?.detail) {
          const detail = data.detail;
          if (Array.isArray(detail) && detail.length > 0) {
            errorMsg = detail[0]?.msg || detail[0]?.message || String(detail[0]);
          } else if (typeof detail === 'string') {
            errorMsg = detail;
          } else if (typeof detail === 'object') {
            errorMsg = detail.message || JSON.stringify(detail);
          }
        } else if (data?.message) {
          errorMsg = data.message;
        } else if (error.message) {
          errorMsg = error.message;
        }

        setErrorMessage(errorMsg);
        toast.error(errorMsg);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Helper function to convert 12-hour format to 24-hour format
  const convertTo24Hour = time12h => {
    if (!time12h) return '';

    try {
      // Handle both "1:30 PM" and "01:30 PM" formats
      const [time, modifier] = time12h.split(' ');
      let [hours, minutes] = time.split(':');

      // Convert hours to number for calculation
      hours = parseInt(hours, 10);

      // Convert to 24-hour format
      if (modifier === 'PM' && hours < 12) {
        hours += 12;
      } else if (modifier === 'AM' && hours === 12) {
        hours = 0;
      }

      // Format hours and minutes with leading zeros
      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    } catch (e) {
      console.error('Error converting time format:', e);
      return time12h;
    }
  };

  const resetForm = () => {
    setDays({
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    });

    setTimeSlots({
      monday: [{ from: '', to: '' }],
      tuesday: [{ from: '', to: '' }],
      wednesday: [{ from: '', to: '' }],
      thursday: [{ from: '', to: '' }],
      friday: [{ from: '', to: '' }],
      saturday: [{ from: '', to: '' }],
      sunday: [{ from: '', to: '' }],
    });

    setDescription('');
    setErrorMessage('');
    setApiError('');
    setAvailabilityData(null);
    setDataFetched(false);
  };

  // Add a function to render the time picker as a portal
  const renderTimePickerPortal = () => {
    if (!activeTimePicker) return null;

    const { day, index, field } = activeTimePicker;
    const timeValue = stringToTime(timeSlots[day][index][field]);

    // Get the reference key for this time picker
    const refKey = `${day}-${index}-${field}`;
    const inputElement = timePickerRefs.current[refKey];

    return ReactDOM.createPortal(
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimePicker
          value={timeValue}
          onChange={newValue => handleTimeChange(day, index, field, newValue)}
          onClose={handleTimePickerClose}
          className="time-picker-portal"
          ampm={true}
          format="hh:mm A"
          minutesStep={15}
          open={true}
          viewRenderers={{
            hours: customTimeViewRenderer,
            minutes: customTimeViewRenderer,
          }}
          views={['hours', 'minutes']}
          view={clockView}
          onViewChange={newView => setClockView(newView)}
          slots={{
            textField: () => null, // Hide the default text field
          }}
          slotProps={{
            popper: {
              style: { zIndex: 2500 },
              placement: 'bottom-start',
              anchorEl: inputElement,
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 5],
                  },
                },
              ],
            },
          }}
        />
      </LocalizationProvider>,
      document.body
    );
  };

  // Modify renderDayTimeSlots to use custom time input wrapper
  const renderDayTimeSlots = day => {
    if (!days[day]) return null;

    return timeSlots[day].map((slot, index) => (
      <Box key={`${day}-${index}`} className="time-slot-container">
        <Box className="time-inputs">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box className="time-input-group-one-on-one">
              <Typography className="time-label">From</Typography>
              <div
                className="time-input-wrapper"
                onClick={() => handleTimePickerOpen(day, index, 'from')}
                ref={el => {
                  // Store the ref for this specific time input field
                  timePickerRefs.current[`${day}-${index}-from`] = el;
                }}
              >
                <div className="time-input-icon">
                  <AccessTimeIcon fontSize="small" />
                </div>
                <input
                  className="time-input-field"
                  value={slot.from || ''}
                  placeholder="Select time"
                  readOnly
                />
              </div>
            </Box>

            <Box className="time-input-group-one-on-one">
              <Typography className="time-label">To</Typography>
              <div
                className="time-input-wrapper"
                onClick={() => handleTimePickerOpen(day, index, 'to')}
                ref={el => {
                  // Store the ref for this specific time input field
                  timePickerRefs.current[`${day}-${index}-to`] = el;
                }}
              >
                <div className="time-input-icon">
                  <AccessTimeIcon fontSize="small" />
                </div>
                <input
                  className="time-input-field"
                  value={slot.to || ''}
                  placeholder="Select time"
                  readOnly
                />
              </div>
            </Box>
          </LocalizationProvider>

          <Box className="time-slot-actions">
            <IconButton className="time-slot-action-button" onClick={() => addTimeSlot(day)}>
              <AddIcon fontSize="small" />
            </IconButton>

            <IconButton
              className="time-slot-action-button"
              onClick={e => openCopyPopover(e, day, index)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>

            <IconButton
              className="time-slot-action-button"
              onClick={() => removeTimeSlot(day, index)}
              disabled={timeSlots[day].length === 1}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {errorMessage && errorMessage.includes(`${day.charAt(0).toUpperCase() + day.slice(1)}`) && (
          <Typography
            className="error-message"
            sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}
          >
            {errorMessage}
          </Typography>
        )}
      </Box>
    ));
  };

  // Render loading state
  const renderLoading = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 5 }}>
      <CircularProgress />
      <Typography variant="body1" sx={{ ml: 2 }}>
        Loading availability data...
      </Typography>
    </Box>
  );

  // Modify the renderError function to not be a blocker
  // const renderError = () => (
  //   <Box sx={{ mb: 2 }}>
  //     <Alert severity="warning" sx={{ mb: 2 }}>
  //       {apiError || 'Some data may not have loaded correctly. You can still continue or retry.'}
  //     </Alert>
  //     <Button variant="outlined" color="primary" onClick={() => fetchData()} sx={{ mr: 1 }}>
  //       Retry Loading Data
  //     </Button>
  //   </Box>
  // );

  return (
    <Modal open={open} onClose={onClose} className="one-on-one-modal">
      <Box className="one-on-one-modal-content">
        {/* Remove conditional rendering for success modal and always show the main content */}
        <>
          <Box className="one-on-one-modal-header">
            <Typography className="one-on-one-modal-title">Edit One on One Schedule</Typography>
            <IconButton onClick={onClose} className="close-button">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box className="one-on-one-modal-body">
            {isLoading && !dataFetched ? (
              renderLoading()
            ) : (
              <>
                <Box className="days-selection">
                  {Object.keys(days).map(day => (
                    <Box key={day} className="day-container">
                      <Box className="day-header">
                        <Typography className="day-name-one-on-one">
                          {day.charAt(0).toUpperCase() + day.slice(1)}
                        </Typography>
                        <Switch
                          checked={days[day]}
                          onChange={() => handleDayToggle(day)}
                          color="primary"
                          className="day-switch-one-on-one"
                        />
                      </Box>
                      {renderDayTimeSlots(day)}
                    </Box>
                  ))}
                </Box>

                <Box className="description-container">
                  <Typography className="description-label">Write a short description</Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Enter here..."
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    variant="outlined"
                    className="description-field"
                  />
                </Box>
              </>
            )}
          </Box>

          <Box className="one-on-one-modal-footer">
            <Button variant="outlined" onClick={onClose} className="cancel-button-one-on-one">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              className="continue-button"
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Save Changes'}
            </Button>
          </Box>

          <Popover
            open={Boolean(copyAnchorEl)}
            anchorEl={copyAnchorEl}
            onClose={closeCopyPopover}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            className="copy-popover"
          >
            <Box className="copy-popover-content">
              <Typography className="copy-popover-title">Copy Times TO</Typography>
              <Box className="copy-days-list">
                {Object.keys(copyTargetDays)
                  .filter(day => day !== copySourceDay)
                  .map(day => (
                    <FormControlLabel
                      key={day}
                      control={
                        <Checkbox
                          checked={copyTargetDays[day]}
                          onChange={() => handleCopyTargetChange(day)}
                          color="primary"
                        />
                      }
                      label={day.charAt(0).toUpperCase() + day.slice(1)}
                      className="copy-day-checkbox"
                    />
                  ))}
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={applyCopy}
                className="apply-copy-button"
              >
                Apply
              </Button>
            </Box>
          </Popover>

          {renderTimePickerPortal()}
        </>
      </Box>
    </Modal>
  );
};

export default EditOneOnOneSchedule;
